import { RegulatorAmlValEventType } from '@ps/platform-message-framework/regulator/RegulatorAmlValEventType';
import { Brand, FeatureFlag, Site } from '@ps/web-core-enums';
import { isEmpty } from 'lodash';
import { useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import type { Store } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';

import type {
  DepositPayload,
  EnvoyBankInfo,
  FirstWizardData,
  PaySystemDetails,
  PsPayBank,
  SavedCards
} from '../../types/Cashier';
import { CashierFlowType } from '../../types/Cashier';
import { CardType } from '../../types/cardType';
import { getSavedMethod } from '../../utils/cashier';

import { noInput, noInputSaved } from './data';

const useDepositGuardsData = () => {
  const locale = useSelector(select.locale.get);
  const pathname = useSelector(select.location.getPathname);
  const query = useSelector(select.location.getQuery);
  const isReloader = useSelector(select.account.isReloader);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const amlEventType = useSelector(select.account.getAmlEventType);
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const nationalExclusion = useSelector(select.account.bannedByRegulator);
  const isSWCWelcomeBonusScreenEnabled = useFeature(FeatureFlag.SwcWelcomeBonusScreen);
  const isSwcMethodVerification = useFeature(FeatureFlag.SwcMethodVerification);
  const isSWCAffiliateBonusEnabled = useFeature(FeatureFlag.SwcAffiliateBonus);
  const isSWCECheckConfirmationEnabled = useFeature(FeatureFlag.SwcECheckConfirmation);
  const currentlyTakingBreak = isSelfExcluded || nationalExclusion;
  const hideBonusCode = useSelector(select.account.hideBonusCode);
  const isSuspendedRM = useSelector(select.account.isSuspendRM);
  const site = useSelector(select.cms.getSiteId);
  const store = useStore() as Store;

  const depositNoDetails = ({
    values: { deposit },
    data: { envoyBanks, savedCards, savedCardInfoId, paySystemDetails, asPaybankInfo }
  }: {
    values: { deposit: DepositPayload };
    data: {
      envoyBanks: EnvoyBankInfo[];
      savedCards: SavedCards;
      savedCardInfoId: number;
      paySystemDetails: PaySystemDetails;
      asPaybankInfo: PsPayBank[];
    };
  }) => {
    const { type } = deposit;

    if (savedCards?.length && savedCardInfoId) {
      const [{ savedCard }] = getSavedMethod(savedCards, savedCardInfoId);
      if (savedCard && noInputSaved.includes(savedCard.paySystem)) {
        return true;
      }
    }
    const hasDefaultAsPaybankMethod = asPaybankInfo?.some((item) => item.default);

    return (
      hasDefaultAsPaybankMethod ||
      envoyBanks.length === 1 ||
      (type === CardType.PayPal && !paySystemDetails.info.supportBillingAgreement) ||
      noInput.includes(type)
    );
  };

  const depositResultContextPresent = useCallback(
    () =>
      pathname.startsWith('/deposit/result') ||
      (pathname.startsWith(`/${locale}/deposit/result`) && !!query),
    [locale, pathname, query]
  );

  const checkSWFFlowGuard = ({
    data: { cashierFlow }
  }: {
    data: { cashierFlow: CashierFlowType };
  }) => cashierFlow === CashierFlowType.FirstDeposit || depositResultContextPresent();

  const depositDetailsConfirmCheck = ({
    data: { savedCardInfoId },
    values: { deposit }
  }: {
    data: { savedCardInfoId: number };
    values: { deposit: DepositPayload };
  }) => {
    const { type } = deposit;
    return isSWCECheckConfirmationEnabled && type === CardType.ECheck && !savedCardInfoId;
  };

  const showItBonusOffer = useCallback(
    ({ data: { signupPassed } }: { data: { signupPassed: boolean } }) => {
      const rootState = store.getState();
      const { registrationBrand } = select.localSettings.get(rootState);
      return registrationBrand !== Brand.BetStars && signupPassed && site === Site.IT;
    },
    [store, site]
  );

  const showWelcomeBonusOffer = ({
    data: { crmBonusCode }
  }: {
    data: {
      crmBonusCode: string;
    };
  }): boolean => {
    const showWelcomeBonus =
      !!isSWCWelcomeBonusScreenEnabled &&
      !!(isSWCAffiliateBonusEnabled || crmBonusCode) &&
      !isReloader &&
      !isMobileEmbedded &&
      !hideBonusCode;

    return showWelcomeBonus;
  };

  const accountSuspended = useCallback(
    () =>
      amlEventType === RegulatorAmlValEventType.ACCOUNT_SUSPENDED ||
      currentlyTakingBreak ||
      isSuspendedRM,
    [amlEventType, currentlyTakingBreak, isSuspendedRM]
  );

  const showPendingCashout = ({ data: { pendingCashouts } }: { data: FirstWizardData }) =>
    !isEmpty(pendingCashouts);

  const depositCashoutMenuSelect = ({
    values: {
      pendingCashouts: { menuSelected }
    }
  }: {
    values: { pendingCashouts: { menuSelected: 'pendingCashout' | 'deposit' } };
  }) => menuSelected === 'pendingCashout';

  const retryDepositCheck = ({ data: { depositResult } }: { data: FirstWizardData }) =>
    !!depositResult?.retry;

  const depositPaymentVerificationCheck = () => isSwcMethodVerification;

  const isRedirectExternalAppContextState = ({ data: { depositResultContext } }: WizardContext) =>
    !!depositResultContext?.appSchemaUrl;

  return {
    accountSuspended,
    checkSWFFlowGuard,
    depositCashoutMenuSelect,
    depositDetailsConfirmCheck,
    depositNoDetails,
    depositPaymentVerificationCheck,
    depositResultContextPresent,
    isRedirectExternalAppContextState,
    showPendingCashout,
    showItBonusOffer,
    showWelcomeBonusOffer,
    retryDepositCheck
  };
};

export default useDepositGuardsData;
